import { useQuery } from '@tanstack/react-query';

import { configService, decodeConfigValue } from '@/services/config.service';
import { queryKeys } from '@/services/query.config';

/** Dynamic config namespace for boolean flags: readable by any authenticated user. */
const FEATURE_FLAG_NAMESPACE = 'feature_flag';

/**
 * Whether a boolean feature flag from the dynamic config store is on.
 *
 * Reads `key` from the `feature_flag` namespace (writable by system admins
 * only — see pkg/dynconfig) and returns true only when its value is the JSON
 * boolean `true`. The caller builds the key, so the same hook reads a global
 * flag (`event_monitoring`) or a per-organization one (`clone_case_<org_id>`).
 *
 * Fails closed: false while loading, on error, without a key, or when the key
 * is absent or holds anything other than `true`.
 *
 * Lists by prefix and matches the exact key rather than GETting the key itself:
 * an absent key is a 404, which the shared apiClient swallows into a null
 * response, whereas the list endpoint answers 200 with an empty page.
 *
 * Keys must not contain `.` or another regex metacharacter: the config
 * server's prefix filter double-escapes them, so the list comes back empty and
 * the flag reads as off. Join per-org keys with `_`, not `.`.
 */
export const useFeatureFlag = (key: string | undefined): boolean =>
  useFeatureFlagState(key).enabled;

/**
 * {@link useFeatureFlag} plus whether the flag is still being read.
 *
 * Use this where "off" and "not known yet" must be told apart — a route
 * validator that redirects when a flag is off would otherwise bounce a deep
 * link before the flag arrives. `enabled` fails closed exactly like
 * useFeatureFlag; `isLoading` is true only while the first read for `key` is
 * in flight (never for an `undefined` key, which sends no request).
 */
export const useFeatureFlagState = (
  key: string | undefined,
): { enabled: boolean; isLoading: boolean } => {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.config.featureFlag(key ?? ''),
    queryFn: async (): Promise<boolean> => {
      const response = await configService.listKeys(FEATURE_FLAG_NAMESPACE, key);
      const entry = response.entries?.find(e => e.key === key);
      return entry ? decodeConfigValue<boolean>(entry.value, false) === true : false;
    },
    enabled: !!key,
    staleTime: 5 * 60 * 1000, // flags change rarely; a different key refetches
    retry: 1,
  });

  return { enabled: data === true, isLoading };
};
